import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
  styled,
  CircularProgress,
} from "@mui/material";
import {
  VisibilityOutlined as VisibilityIcon,
  GetApp as DownloadIcon,
  InsertDriveFile as FileIcon,
} from "@mui/icons-material";
import { awsDateToUS } from "utils/dateUtils";
import { downloadDocument, formatFileSize } from "utils/supplementalDocuments";

// Styled Components
const PolicyTypeChip = styled(Chip)(({ theme }) => ({
  fontSize: "0.65rem",
  height: 20,
  backgroundColor: theme.palette.info.light,
  color: theme.palette.info.dark,
  margin: "0 2px",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  fontSize: "0.75rem",
  padding: "2px 6px",
  minWidth: 0,
  textTransform: "none",
  ...(variant === "view" && {
    color: theme.palette.primary.main,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
  ...(variant === "download" && {
    color: theme.palette.text.secondary,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  }),
}));

const SupplementalDocuments = ({ documents = [], onViewDocument, loading = false }) => {
  if (loading) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading supplemental documents...
        </Typography>
      </Box>
    );
  }
  
  if (!documents || documents.length === 0) {
    return (
      <Box sx={{ py: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No supplemental documents available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ml: 8, mt: 1, mb: 1.5, pr: 2 }}>
      <Typography variant="subtitle2" component="div" color="text.secondary" sx={{ mb: 0.5 }}>
        Supplemental Documents
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "30%", border: 0, py: 0.5, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Document File
              </Typography>
            </TableCell>
            <TableCell sx={{ width: "20%", border: 0, py: 0.5, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Policy Type
              </Typography>
            </TableCell>
            <TableCell sx={{ width: "20%", border: 0, py: 0.5, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Uploaded Date
              </Typography>
            </TableCell>
            <TableCell sx={{ width: "15%", border: 0, py: 0.5, px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                File Size
              </Typography>
            </TableCell>
            <TableCell sx={{ width: "15%", border: 0, py: 0.5, px: 1, textAlign: "right" }}>
              <Typography variant="caption" color="text.secondary">
                Actions
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((document, index) => (
            <TableRow key={document.id || index}>
              <TableCell sx={{ borderBottom: "1px solid", borderBottomColor: "grey.200", py: 1, px: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FileIcon fontSize="small" color="action" sx={{ mr: 0.5, fontSize: 18 }} />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontSize: "0.875rem" }}>
                    {document.filename}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ borderBottom: "1px solid", borderBottomColor: "grey.200", py: 1, px: 1 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.3 }}>
                  {document.policyTypes && document.policyTypes.length > 0 ? (
                    document.policyTypes.map((type) => (
                      <PolicyTypeChip key={type} label={type} size="small" />
                    ))
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ borderBottom: "1px solid", borderBottomColor: "grey.200", py: 1, px: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                  {document.uploadedDate ? awsDateToUS(document.uploadedDate) : "-"}
                </Typography>
              </TableCell>
              <TableCell sx={{ borderBottom: "1px solid", borderBottomColor: "grey.200", py: 1, px: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.875rem" }}>
                  {document.fileSize ? formatFileSize(document.fileSize) : "-"}
                </Typography>
              </TableCell>
              <TableCell sx={{ borderBottom: "1px solid", borderBottomColor: "grey.200", py: 1, px: 1, textAlign: "right" }}>
                <Box sx={{ display: "flex", gap: 0.3, justifyContent: "flex-end" }}>
                  <ActionButton
                    variant="view"
                    size="small"
                    startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onViewDocument(document)}
                  >
                    View
                  </ActionButton>
                  <ActionButton
                    variant="download"
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={() => downloadDocument(document)}
                  >
                    Download
                  </ActionButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default SupplementalDocuments;